import React, { useRef, useState } from "react";
import { Paperclip, X } from "lucide-react";
import Button from "../forms/Button";
import { ACCEPTED_FILES, checkFile, formatSize } from "./attachments";
import TrelloEditor from "./TrelloEditor";

const newDraftId = () =>
  (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/**
 * Rich comment input (Trello-style editor) with @mentions and files.
 *  - Ctrl/⌘+Enter submits.
 *  - "@" (or the @ button) suggests people who can see the task; picks
 *    become mention chips and are sent as structured ids.
 *  - Each draft has a client request id, so a retried submit can't duplicate.
 *  - The input is cleared only after the server accepted the comment.
 */
const CommentComposer = ({
  mentionable = [],
  onSubmit,
  allowFiles = true,
  initialBody = "",
  submitLabel = "Save",
  onCancel,
  autoFocus = false,
  placeholder = "Write a comment…",
}) => {
  const [body, setBody] = useState(initialBody);
  const [editorKey, setEditorKey] = useState(0);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");
  const draftId = useRef(newDraftId());
  const editor = useRef(null);
  const fileInput = useRef(null);

  const addFiles = (list) => {
    const chosen = Array.from(list || []);
    if (fileInput.current) fileInput.current.value = "";
    const problem = chosen.map(checkFile).find(Boolean);
    if (problem) return setError(problem);
    setError("");
    setFiles((prev) => [...prev, ...chosen].slice(0, 5));
  };

  const submit = async () => {
    const empty = editor.current?.isEmpty() ?? !body;
    if (submitting || (empty && !files.length)) return;
    const mentions = editor.current?.getMentionIds() || [];
    setSubmitting(true);
    setError("");
    try {
      await onSubmit(
        { body: empty ? "" : body, mentions, files, clientRequestId: draftId.current },
        (e) =>
          setProgress(e.total ? Math.round((e.loaded / e.total) * 100) : null),
      );
      setBody("");
      setFiles([]);
      setEditorKey((k) => k + 1);
      draftId.current = newDraftId();
    } catch (err) {
      setError(err?.message || "Could not send the comment");
    } finally {
      setSubmitting(false);
      setProgress(null);
    }
  };

  return (
    <div className="space-y-2">
      <TrelloEditor
        key={editorKey}
        ref={editor}
        value={initialBody}
        onChange={setBody}
        placeholder={placeholder}
        mentionable={mentionable}
        onSubmit={submit}
        autoFocus={autoFocus}
        disabled={submitting}
        compact
        minHeight={64}
        ariaLabel="Comment"
      />

      {files.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {files.map((f, i) => (
            <li
              key={`${f.name}-${i}`}
              className="flex items-center gap-1.5 text-xs px-2 py-1 rounded-lg bg-raise text-muted max-w-full"
            >
              <Paperclip className="w-3 h-3 shrink-0" />
              <span className="truncate max-w-[160px]" title={f.name}>
                {f.name}
              </span>
              <span className="text-gray-400">{formatSize(f.size)}</span>
              <button
                type="button"
                onClick={() =>
                  setFiles((prev) => prev.filter((_, j) => j !== i))
                }
                disabled={submitting}
                aria-label={`Remove ${f.name}`}
                className="hover:text-red-600"
              >
                <X className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {progress !== null && files.length > 0 && (
        <div className="h-1 rounded-full bg-raise overflow-hidden">
          <div
            className="h-full bg-grad transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
      {error && (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}

      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {allowFiles && (
            <>
              <input
                ref={fileInput}
                type="file"
                multiple
                accept={ACCEPTED_FILES}
                className="hidden"
                onChange={(e) => addFiles(e.target.files)}
              />
              <button
                type="button"
                onClick={() => fileInput.current?.click()}
                disabled={submitting || files.length >= 5}
                className="p-1.5 rounded-lg text-faint hover:text-accent hover:bg-accent-soft disabled:opacity-50"
                aria-label="Attach files"
                title="Attach files"
              >
                <Paperclip className="w-4 h-4" />
              </button>
            </>
          )}
          <span className="hidden sm:inline font-mono text-[11px] text-faint whitespace-nowrap">
            Ctrl/⌘ + Enter to send
          </span>
        </div>
        <div className="flex items-center gap-2">
          {onCancel && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancel
            </Button>
          )}
          <Button
            size="sm"
            onClick={submit}
            disabled={submitting || (!body && !files.length)}
          >
            {submitting ? "Sending…" : submitLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CommentComposer;
